import React, { useState, FormEvent, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/config";
import { useAuth } from "../../utils/AuthContext";
import Config from "../../Config";
import { useSendEmail } from "../../hooks/useSendEmail";
import { verificationCodeEmail } from "../../emails";

import { Eye, EyeOff } from "lucide-react";

import PrimaryButton from "../../components/Buttons/PrimaryButton";
import SecondaryButton from "../../components/Buttons/SecondaryButton";
import PasswordStrengthMeter, {
  getPasswordScore,
} from "../../components/PasswordStrengthMeter";

const Signup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { sendEmail } = useSendEmail();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate("/home/dashboard", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const checkEmailVerification = async () => {
      const user = auth.currentUser;
      if (user && !authLoading) {
        await user.reload();
        if (!user.emailVerified) {
          sessionStorage.setItem("pendingVerificationEmail", user.email || "");
          navigate("/verify-code", { replace: true });
        }
      }
    };
    checkEmailVerification();
  }, [navigate, authLoading]);

  const handleSignup = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError("");
    setLoading(true);

    if (!name.trim()) {
      setApiError("Please enter your name.");
      setLoading(false);
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setApiError("Please enter a valid email address.");
      setLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setApiError("Passwords do not match.");
      setLoading(false);
      return;
    }
    if (getPasswordScore(password) < 3) {
      setApiError("Please choose a stronger password.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      await setDoc(doc(db, "users", user.uid), {
        name: name.trim(),
        email: email,
        createdAt: new Date().toISOString(),
      });

      const codeRes = await fetch(`${Config.API_URL}/api/auth/send-verification-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, firebase_uid: user.uid }),
      });
      const codeData = await codeRes.json();
      await sendEmail({ to: email, template: verificationCodeEmail({ code: codeData.code }) });

      sessionStorage.setItem("pendingVerificationEmail", email);
      sessionStorage.setItem("pendingVerificationName", name.trim());

      if (searchParams.get("plan") === "individual") {
        localStorage.setItem("pendingPlan", "individual");
      }

      navigate("/verify-code");
    } catch (error: any) {
      console.error("Signup error:", error);

      if (error.code === "auth/email-already-in-use") {
        setApiError("This email is already registered. Please login instead.");
      } else {
        setApiError(error.message || "Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50">
      <div className="w-full max-w-md px-4">
        <div className="text-left">
          <h2 className="text-3xl font-semibold mb-4 text-left">
            Create an account
          </h2>

          <div className="max-w-md mx-auto">
            <form onSubmit={handleSignup}>
              {apiError && (
                <div
                  className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
                  role="alert"
                >
                  {apiError}
                </div>
              )}

              <div className="mt-2">
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <PasswordStrengthMeter password={password} minScore={3} />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Confirm password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="mt-4 flex items-start">
                <input
                  type="checkbox"
                  className="mt-1 mr-2 h-4 w-4 text-[#1d365d] border-gray-300 rounded focus:ring-[#1d365d] disabled:cursor-not-allowed"
                  id="termsCheck"
                  required
                  disabled={loading}
                />
                <label className="text-sm" htmlFor="termsCheck">
                  By creating an account, you agree to our{" "}
                  <Link
                    to="/legal/terms-of-service"
                    className="text-[#1d365d] hover:underline"
                  >
                    Terms & Conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/legal/privacy-policy"
                    className="text-[#1d365d] hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </label>
              </div>

              <PrimaryButton
                className="mt-4 w-full"
                type="submit"
                disabled={loading}
              >
                {loading ? "Creating account..." : "Sign up"}
              </PrimaryButton>
            </form>

            <hr className="my-6 border-gray-300" />

            <h2 className="text-2xl font-semibold mt-4 text-center">
              Already have an account?
            </h2>
            <p className="mt-2 text-gray-600 text-center">
              Sign in to access your personalised dashboard, explore analytics,
              and continue transforming your data.
            </p>

            <SecondaryButton
              className="mt-5 w-full"
              onClick={() => navigate("/login")}
            >
              Sign in
            </SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
